"use client";

import { motion } from "framer-motion";
import { Code, Terminal, Key } from "lucide-react";

export default function ApiServicesPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
          Poker大脑 API
        </h1>
        <p className="text-xl text-gray-400">
          将世界顶级的扑克 AI 嵌入您的应用程序。支持 RESTful 与 WebSocket。
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left Col: Info */}
        <div className="space-y-8">
          <div className="glass-panel p-8 rounded-xl">
            <Key className="w-10 h-10 text-neon-cyan mb-4" />
            <h3 className="text-2xl font-bold mb-3">极速接入</h3>
            <p className="text-gray-400 leading-relaxed">
              获取 API Key 后，只需几行代码即可开始发送牌局状态并获取 AI 的决策建议。我们的边缘计算节点确保全球范围内的延迟均低于 200ms。
            </p>
          </div>

          <div className="glass-panel p-8 rounded-xl">
            <Terminal className="w-10 h-10 text-neon-magenta mb-4" />
            <h3 className="text-2xl font-bold mb-3">支持的接口能力</h3>
            <ul className="space-y-3 text-gray-400">
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-neon-cyan"></div>
                实时牌局决策 (Action Recommendation)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-neon-cyan"></div>
                手牌胜率分析 (Equity Calculation)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-neon-cyan"></div>
                对手档案构建 (Opponent Profiling)
              </li>
              <li className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-neon-cyan"></div>
                复盘与 EV 评估 (Session Review)
              </li>
            </ul>
          </div>
        </div>

        {/* Right Col: Code Snippet */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="rounded-xl overflow-hidden border border-white/10 bg-[#0d0d0d] shadow-2xl"
        >
          <div className="flex items-center px-4 py-3 border-b border-white/10 bg-black/50">
            <div className="flex gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <span className="ml-4 text-xs text-gray-500 font-mono">request.js</span>
          </div>
          <div className="p-6 overflow-x-auto text-sm font-mono leading-loose text-gray-300">
            <pre><code>
<span className="text-pink-400">const</span> axios = <span className="text-blue-400">require</span>(<span className="text-green-400">'axios'</span>);{'\n\n'}
<span className="text-gray-500">// 构建牌局状态</span>{'\n'}
<span className="text-pink-400">const</span> gameState = {'{\n'}
{'  '}gameType: <span className="text-green-400">'NLH'</span>,{'\n'}
{'  '}holeCards: [<span className="text-green-400">'As'</span>, <span className="text-green-400">'Kd'</span>],{'\n'}
{'  '}boardCards: [<span className="text-green-400">'Ts'</span>, <span className="text-green-400">'Jc'</span>, <span className="text-green-400">'2h'</span>],{'\n'}
{'  '}potSize: <span className="text-purple-400">150</span>,{'\n'}
{'  '}activePlayers: <span className="text-purple-400">2</span>,{'\n'}
{'  '}position: <span className="text-green-400">'BTN'</span>{'\n'}
{'}'};{'\n\n'}
<span className="text-gray-500">// 请求 Poker大脑 API</span>{'\n'}
<span className="text-pink-400">const</span> response = <span className="text-pink-400">await</span> axios.post({'\n'}
{'  '}<span className="text-green-400">'https://api.pokerrobot.ai/v1/decision'</span>,{'\n'}
{'  '}gameState,{'\n'}
{'  '}{'{'} headers: {'{'} <span className="text-green-400">'Authorization'</span>: <span className="text-green-400">'Bearer YOUR_API_KEY'</span> {'}'} {'}'}{'\n'}
);{'\n\n'}
<span className="text-blue-400">console</span>.log(response.data.action);{'\n'}
<span className="text-gray-500">// Output: {'{'} type: 'Raise', amount: 100, ev: 45.2 {'}'}</span>
            </code></pre>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
